import React from 'react';
import { useToggle, useList, } from 'react-use';
import { keyBy, sortBy, difference, uniq, sumBy, } from 'lodash';
import { Input, Button, } from 'reactstrap';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { toast } from 'react-toastify';
import { addSeconds, } from 'date-fns';

import firebase from '../firebase';
import { batch, getCollectionData, } from '../shared/firebase';
import { createOperationLogs, } from '../utils';
import { numberFormat } from '../util';
import { generateRows as generateTrialRows, } from '../shared/lib/individualTrials';
import { inheritTypes, } from '../shared/models/individualAdjustmentJournal';
import { amountSources, } from '../shared/models/individualAdjustmentJournalTemplate';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import IndividualAdjustmentJournalTemplateFormModal from './modals/IndividualAdjustmentJournalTemplateFormModal';
import ProgressButton from './ProgressButton';
import AddButton from './AddButton';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';

const db = firebase.firestore();

export default function IndividualAdjustmentJournalTemplates(props) {
  const { company, relatedCompany, subsidiaryId, period, yearMonth, isLockedMonth, consolidationAccountItems, sections, changeTypes, customChangeFields, ordersByModelType, individualAdjustmentJournalsRef, } = props;
  const consolidationAccountItemsById = keyBy(consolidationAccountItems, 'id');
  const sectionsById = keyBy(sections, 'id');
  const [isDragging, toggleDragging] = useToggle(false);
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds, }] = useList([]);
  const unselectId = _ => removeSelectedIdAt(selectedIds.indexOf(_));
  const templatesRef = company.ref.collection('individualAdjustmentJournalTemplates');
  const unsortedTemplates = useCollectionSubscription(templatesRef.where('subsidiaryId', '==', subsidiaryId), [company, subsidiaryId]);
  const templates = sortBy(unsortedTemplates, 'index');
  const isAllSelected = templates.length > 0 && difference(templates.map(_ => _.id), selectedIds).length === 0;

  const accountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', subsidiaryId), [company, subsidiaryId]);
  const accountItemsById = keyBy(accountItems, 'id');
  const trials = useCollectionSubscription(company.ref.collection('trials').where('subsidiaryId', '==', subsidiaryId).where('yearMonth', '==', yearMonth), [company, subsidiaryId, yearMonth]);

  const trialBalanceByAccountItemId = (_ => {
    const rows = generateTrialRows(relatedCompany, trials, accountItems, consolidationAccountItems);
    const rowsWithItem = rows.filter(_ => _.accountItem != null);
    return keyBy(
      uniq(rowsWithItem.map(_ => _.accountItem.id)).map((id) => {
        const balance = sumBy(rowsWithItem.filter(_ => _.accountItem.id === id), _ => _.closing_balance || 0);
        return { id, balance };
      }),
      'id'
    );
  })();

  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMoveImmutable(templates, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => batch.update(templatesRef.doc(id), { index }));
  };

  const resolveAmount = (template) => {
    if(template.amountSource !== 'trialBalance') return null;
    return trialBalanceByAccountItemId[template.amountReferenceItemId]?.balance ?? 0;
  };

  const onClickApply = async () => {
    if(selectedIds.length === 0) return;
    if(!window.confirm(`${selectedIds.length}件のテンプレートから修正仕訳を登録しますか？`)) return;

    const selectedTemplates = sortBy(templates.filter(_ => selectedIds.includes(_.id)), 'index');
    const createdRefs = [];
    await batch(db, selectedTemplates, (batch, template, i) => {
      const amount = resolveAmount(template);
      const items = (template.items || []).map((item) => {
        return amount == null ? item : { ...item, debitAmount: amount, creditAmount: amount, };
      });
      const ref = individualAdjustmentJournalsRef.doc();
      createdRefs.push(ref);
      batch.set(ref, {
        inheritType: template.inheritType || 'none',
        description: template.description || '',
        items,
        changeTypes: uniq(items.flatMap(_ => [_.debitChangeType, _.creditChangeType]).filter(_ => _)),
        subsidiaryId,
        period,
        yearMonth,
        templateId: template.id,
        createdAt: addSeconds(new Date(), i),
      });
    });
    await createOperationLogs(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', createdRefs.map(_ => ({ yearMonth, individualAdjustmentJournalId: _.id })));
    toast.success(`${selectedTemplates.length}件の修正仕訳を登録しました`);
    setSelectedIds([]);
  };

  const formProps = { consolidationAccountItems, accountItems, sections, relatedCompany, customChangeFields, ordersByModelType, };

  return (
    <div className="individual-adjustment-journal-templates">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          {selectedIds.length > 0 && (
            <>
              <span>{selectedIds.length}件選択中</span>
              <ProgressButton color="success" process={onClickApply} disabled={isLockedMonth}>
                <span className="fas fa-check mr-1" />
                登録
              </ProgressButton>
              <Button color="secondary" onClick={_ => setSelectedIds([])}>選択解除</Button>
            </>
          )}
        </div>
        <AddButton itemRef={templatesRef.doc()} processValues={_ => ({ ..._, subsidiaryId, index: templates.length, })} FormModal={IndividualAdjustmentJournalTemplateFormModal} initialValues={{ items: [{}], }} formProps={formProps} disabled={isLockedMonth} />
      </div>
      <div>
        <table className="table sticky-table">
          <thead className="thead-light text-center">
            <tr>
              <th style={{ width: 30 }}>
                <Input type="checkbox" className="position-relative m-0" checked={isAllSelected} onChange={_ => isAllSelected ? setSelectedIds([]) : setSelectedIds(templates.map(_ => _.id))} />
              </th>
              <th></th>
              <th style={{ minWidth: 150 }}>名称</th>
              <th style={{ minWidth: 100 }}>金額</th>
              <th style={{ minWidth: 220 }}>借方</th>
              <th style={{ minWidth: 220 }}>貸方</th>
              <th style={{ minWidth: 80 }}>引継</th>
              <th></th>
            </tr>
          </thead>
          <Container
            dragHandleSelector=".drag-handle"
            onDrop={onDrop}
            onDragStart={_ => toggleDragging(true)}
            onDragEnd={_ => toggleDragging(false)}
            dropPlaceholder={{ style: { background: 'eee', } }}
            render={(ref) => (
              <tbody ref={ref}>
                {
                  templates.map((template) => {
                    const { id, ref, name, inheritType = 'none', amountSource = 'fixed', amountReferenceItemId, items = [], } = template;
                    const amount = resolveAmount(template);
                    const referenceItem = accountItemsById[amountReferenceItemId];

                    return (
                      <Draggable
                        key={id}
                        render={() => (
                          <tr style={{ display: !isDragging && 'table-row' }}>
                            <td style={{ width: 30 }}>
                              <Input type="checkbox" className="position-relative m-0" checked={selectedIds.includes(id)} onChange={_ => selectedIds.includes(id) ? unselectId(id) : selectId(id)} />
                            </td>
                            <td style={{ width: 30 }}>
                              <div className="drag-handle text-muted cursor-pointer">
                                <span className="fas fa-grip-vertical" />
                              </div>
                            </td>
                            <td>{name}</td>
                            <td className="small">
                              <div>{amountSources[amountSource]?.label}</div>
                              {
                                amountSource === 'trialBalance' && (
                                  <>
                                    <div className="text-muted">{referenceItem?.name}</div>
                                    <div className="font-weight-bold text-right">{numberFormat(relatedCompany.currency, amount || 0)}</div>
                                  </>
                                )
                              }
                            </td>
                            <td className="small">
                              {
                                items.map((item, i) => (
                                  <div key={i} className="d-flex justify-content-between gap-2">
                                    <div>{consolidationAccountItemsById[item.debitItemId]?.name}</div>
                                    <div className="text-muted">{sectionsById[item.debitSectionId]?.name}</div>
                                    <div className="text-muted">{changeTypes[item.debitChangeType]?.label}</div>
                                    {amountSource === 'fixed' && <div>{numberFormat(relatedCompany.currency, item.debitAmount || 0)}</div>}
                                  </div>
                                ))
                              }
                            </td>
                            <td className="small">
                              {
                                items.map((item, i) => (
                                  <div key={i} className="d-flex justify-content-between gap-2">
                                    <div>{consolidationAccountItemsById[item.creditItemId]?.name}</div>
                                    <div className="text-muted">{sectionsById[item.creditSectionId]?.name}</div>
                                    <div className="text-muted">{changeTypes[item.creditChangeType]?.label}</div>
                                    {amountSource === 'fixed' && <div>{numberFormat(relatedCompany.currency, item.creditAmount || 0)}</div>}
                                  </div>
                                ))
                              }
                            </td>
                            <td className="small">
                              {inheritType !== 'none' && <div className="badge badge-info">{inheritTypes[inheritType]?.label}</div>}
                            </td>
                            <td className="text-nowrap text-right">
                              <div className="d-flex gap-1 justify-content-end">
                                <EditButton size="sm" itemRef={ref} FormModal={IndividualAdjustmentJournalTemplateFormModal} formProps={{ ...formProps, values: template, }} disabled={isLockedMonth} />
                                <DeleteButton size="sm" item={template} itemRef={ref} disabled={isLockedMonth} />
                              </div>
                            </td>
                          </tr>
                        )}
                      />
                    );
                  })
                }
              </tbody>
            )}
          />
        </table>
      </div>
    </div>
  );
}
